import { useEffect, useMemo, useRef, useState } from "react";
import "../../../css/Adviser/AdviserDirectory.css";
import "../../../css/Adviser/AdviserDirectoryRegulatoryText.css";
import { subscribeAdvisersData } from "../../../context/Adviser/Utils/ExportAdvisersData";
import Sidebar from "../../DesignSystem/Sidebar";
import FilterDrawer, {
  FilterDrawerApplyButton,
  FilterDrawerCheckboxOption,
  FilterDrawerEmpty,
  FilterDrawerPill,
  FilterDrawerPills,
  FilterDrawerResetButton,
  FilterDrawerSection,
} from "../../DesignSystem/FilterDrawer/FilterDrawer";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import {
  safe,
  asList,
  fmtMonthYear,
  fmtHuman,
  formatAddress,
  formatUKPhone,
  displayName,
  initialsFrom,
  cssColor,
  norm,
} from "../Utils/AdviserFormatters";
import { fetchLogoUrl } from "../../../context/Adviser/Utils/FetchLogo";
import { updateAdviserRegulatoryText } from "../../../context/Adviser/Utils/UpdateRegulatoryText";
import { ReactComponent as HistoryIcon } from "../../../assets/icons/HistoryIcon.svg";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import { saveAs } from "file-saver";
import { exportAdviserColoursExcel } from "../Utils/exportAdviserColoursExcel";
import { exportAdviserLogosZip } from "../Utils/exportAdviserLogosZip";
import { exportArFcaNumbersExcel } from "../Utils/exportArFcaNumbersExcel";
import { ADVISER_CATCH_UP_POINTS_OF_CONTACT } from "../../../constants/adviserCatchUp";
import { getAdviserCatchUpAssignmentData } from "../Tabs/AdviserCatchUp/adviserCatchUpService";
import {
  formatAdviserCatchUpPersonName,
  isConfiguredAdviserCatchUpUser,
  resolveAdviserCatchUpPrimaryEmail,
  resolveAssignedStaffEmails,
} from "../Tabs/AdviserCatchUp/adviserCatchUpConfig";

const matchesQuery = (a, q) => {
  if (!q) return true;
  const hay = [
    a.firstName,
    a.preferredName,
    a.lastName,
    a.email,
    a.mobileNumber,
    a.officeNumber,
    a.fcaNumber,
    a.businessAddress,
    a.tradingStyle,
    a.tradingName,
    a.website,
    ...(a.status || []),
    ...(a.tradingStyles || []),
    ...(a.businessAreas || []),
    ...(a.ARBusinessAreas || []),
    a.regulatoryText || "",
    a.isAR ? "ar" : "",
    a.isIndroducerAR ? "iar introducer ar" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return norm(hay).includes(norm(q));
};

const ADVISER_LIST_SCROLL_KEY = "adviserDirectory:listScrollTop";
const SELECTED_ADVISER_KEY = "adviserDirectory:selectedAdviserId";
const ADVISER_FILTERS_KEY = "adviserDirectory:filters";
const NO_PRIMARY_CONTACT_FILTER = "__none__";
const EMPTY_ADVISER_FILTERS = {
  arOnly: false,
  iarOnly: false,
  nonArOnly: false,
  unappointedOnly: false,
  selectedStatuses: [],
  tradingStyle: "",
  businessArea: "",
  assignedStaffEmails: [],
  primaryContactEmails: [],
};

const toFilterSnapshot = (filters) => ({
  arOnly: filters.arOnly,
  iarOnly: filters.iarOnly,
  nonArOnly: filters.nonArOnly,
  unappointedOnly: filters.unappointedOnly,
  selectedStatuses: [...filters.selectedStatuses],
  tradingStyle: filters.tradingStyle,
  businessArea: filters.businessArea,
  assignedStaffEmails: [...filters.assignedStaffEmails],
  primaryContactEmails: [...filters.primaryContactEmails],
});

const countActiveFilters = (filters) =>
  Number(filters.arOnly) +
  Number(filters.iarOnly) +
  Number(filters.nonArOnly) +
  Number(filters.unappointedOnly) +
  filters.selectedStatuses.length +
  Number(Boolean(filters.tradingStyle)) +
  Number(Boolean(filters.businessArea)) +
  filters.assignedStaffEmails.length +
  filters.primaryContactEmails.length;

const toggleValue = (values, value) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

const matchesPrimaryContactFilter = (selectedEmails, primaryEmail) =>
  !selectedEmails.length ||
  selectedEmails.includes(primaryEmail) ||
  (!primaryEmail && selectedEmails.includes(NO_PRIMARY_CONTACT_FILTER));

const getUniqueSortedValues = (items = [], getValues) => {
  const values = new Set();

  (items || []).forEach((item) => {
    const rawValues = getValues(item);
    const list = Array.isArray(rawValues) ? rawValues : [rawValues];

    list.forEach((value) => {
      const label = String(value || "").trim();
      if (label) values.add(label);
    });
  });

  return Array.from(values).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
};

const getSavedFilters = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(ADVISER_FILTERS_KEY) || "{}");
    return {
      arOnly: saved.arOnly === true,
      iarOnly: saved.iarOnly === true,
      nonArOnly: saved.nonArOnly === true,
      unappointedOnly: saved.unappointedOnly === true,
      selectedStatuses: Array.isArray(saved.selectedStatuses)
        ? saved.selectedStatuses.filter((status) => typeof status === "string")
        : [],
      tradingStyle: typeof saved.tradingStyle === "string" ? saved.tradingStyle : "",
      businessArea:
        typeof saved.businessArea === "string" ? saved.businessArea : "",
      assignedStaffEmails: Array.isArray(saved.assignedStaffEmails)
        ? saved.assignedStaffEmails.filter((email) => typeof email === "string")
        : [],
      primaryContactEmails: Array.isArray(saved.primaryContactEmails)
        ? saved.primaryContactEmails.filter((email) => typeof email === "string")
        : [],
    };
  } catch {
    return {
      arOnly: false,
      iarOnly: false,
      nonArOnly: false,
      unappointedOnly: false,
      selectedStatuses: [],
      tradingStyle: "",
      businessArea: "",
      assignedStaffEmails: [],
      primaryContactEmails: [],
    };
  }
};

export default function AdviserDirectory() {
  const { userPermissions } = useUserPermissions();
  const { userEmail } = useUserEmail();
  const canSeeCatchUpFilters =
    userPermissions.includes("Administrator") ||
    (userPermissions.includes("Adviser Support") &&
      isConfiguredAdviserCatchUpUser(userEmail));
  const [savedFilters] = useState(getSavedFilters);
  const [loading, setLoading] = useState(true);
  const [advisers, setAdvisers] = useState([]);
  const [catchUpAssignments, setCatchUpAssignments] = useState({});
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(
    () => sessionStorage.getItem(SELECTED_ADVISER_KEY) || null,
  );
  const [arOnly, setArOnly] = useState(savedFilters.arOnly);
  const [iarOnly, setIarOnly] = useState(savedFilters.iarOnly);
  //   const ALL = "All";
  //   const [statusFilter, setStatusFilter] = useState(ALL);
  const [selectedStatuses, setSelectedStatuses] = useState(
    savedFilters.selectedStatuses,
  ); // e.g. ["FP","MA"]
  const [tradingStyleFilter, setTradingStyleFilter] = useState(
    savedFilters.tradingStyle,
  );
  const [businessAreaFilter, setBusinessAreaFilter] = useState(
    savedFilters.businessArea,
  );
  const [assignedStaffFilter, setAssignedStaffFilter] = useState(
    canSeeCatchUpFilters ? savedFilters.assignedStaffEmails : [],
  );
  const [primaryContactFilter, setPrimaryContactFilter] = useState(
    canSeeCatchUpFilters ? savedFilters.primaryContactEmails : [],
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(() =>
    toFilterSnapshot({
      arOnly: savedFilters.arOnly,
      iarOnly: savedFilters.iarOnly,
      nonArOnly: savedFilters.nonArOnly,
      unappointedOnly: savedFilters.unappointedOnly,
      selectedStatuses: savedFilters.selectedStatuses,
      tradingStyle: savedFilters.tradingStyle,
      businessArea: savedFilters.businessArea,
      assignedStaffEmails: savedFilters.assignedStaffEmails,
      primaryContactEmails: savedFilters.primaryContactEmails,
    }),
  );
  const [nonArOnly, setNonArOnly] = useState(savedFilters.nonArOnly);
  const [unappointedOnly, setUnappointedOnly] = useState(
    savedFilters.unappointedOnly,
  );
  const appliedAssignedStaffFilter = useMemo(
    () => (canSeeCatchUpFilters ? assignedStaffFilter : []),
    [canSeeCatchUpFilters, assignedStaffFilter],
  );
  const appliedPrimaryContactFilter = useMemo(
    () => (canSeeCatchUpFilters ? primaryContactFilter : []),
    [canSeeCatchUpFilters, primaryContactFilter],
  );
  const [downloadingColours, setDownloadingColours] = useState(false);
  const [downloadingLogos, setDownloadingLogos] = useState(false);
  const [downloadingArFcaNumbers, setDownloadingArFcaNumbers] = useState(false);
  const [logoDownloadError, setLogoDownloadError] = useState("");
  const [logoCache, setLogoCache] = useState(() => new Map());
  const listScrollRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  const navigate = useNavigate();


  const statusOptions = useMemo(() => {
    const set = new Set();
    (advisers || []).forEach((a) =>
      (a.status || []).forEach((s) => set.add(s)),
    );
    return Array.from(set).sort();
  }, [advisers]);

  const tradingStyleOptions = useMemo(
    () =>
      getUniqueSortedValues(advisers, (a) => [
        a.tradingStyle,
        a.tradingName,
        ...(a.tradingStyles || []),
      ]),
    [advisers],
  );

  const businessAreaOptions = useMemo(
    () =>
      getUniqueSortedValues(advisers, (a) => [
        ...(a.businessAreas || []),
        ...(a.ARBusinessAreas || []),
      ]),
    [advisers],
  );

  const toggleArOnly = () => {
    setArOnly((previous) => {
      const next = !previous;
      if (next) {
        setIarOnly(false);
        setNonArOnly(false);
        setUnappointedOnly(false);
      }
      return next;
    });
  };

  const toggleIarOnly = () => {
    setIarOnly((previous) => {
      const next = !previous;
      if (next) {
        setArOnly(false);
        setNonArOnly(false);
        setUnappointedOnly(false);
      }
      return next;
    });
  };

  const toggleNonArOnly = () => {
    setNonArOnly((previous) => {
      const next = !previous;
      if (next) {
        setArOnly(false);
        setIarOnly(false);
        setUnappointedOnly(false);
      }
      return next;
    });
  };

  const toggleUnappointedOnly = () => {
    setUnappointedOnly((previous) => {
      const next = !previous;
      if (next) {
        setArOnly(false);
        setIarOnly(false);
        setNonArOnly(false);
        setSelectedStatuses([]);
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setTradingStyleFilter("");
    setBusinessAreaFilter("");
    setArOnly(false);
    setIarOnly(false);
    setNonArOnly(false);
    setUnappointedOnly(false);
    setAssignedStaffFilter([]);
    setPrimaryContactFilter([]);
    setDraftFilters(EMPTY_ADVISER_FILTERS);
  };

  const applyDraftFilters = (nextFilters) => {
    setArOnly(nextFilters.arOnly);
    setIarOnly(nextFilters.iarOnly);
    setNonArOnly(nextFilters.nonArOnly);
    setUnappointedOnly(nextFilters.unappointedOnly);
    setSelectedStatuses(nextFilters.selectedStatuses);
    setTradingStyleFilter(nextFilters.tradingStyle);
    setBusinessAreaFilter(nextFilters.businessArea);
    setAssignedStaffFilter(
      canSeeCatchUpFilters ? nextFilters.assignedStaffEmails : [],
    );
    setPrimaryContactFilter(
      canSeeCatchUpFilters ? nextFilters.primaryContactEmails : [],
    );
  };

  const openFilters = () => {
    setDraftFilters({
      arOnly,
      iarOnly,
      nonArOnly,
      unappointedOnly,
      selectedStatuses: [...selectedStatuses],
      tradingStyle: tradingStyleFilter,
      businessArea: businessAreaFilter,
      assignedStaffEmails: [...appliedAssignedStaffFilter],
      primaryContactEmails: [...appliedPrimaryContactFilter],
    });
    setFiltersOpen(true);
  };

  const activeFilters = {
    arOnly,
    iarOnly,
    nonArOnly,
    unappointedOnly,
    selectedStatuses,
    tradingStyle: tradingStyleFilter,
    businessArea: businessAreaFilter,
    assignedStaffEmails: appliedAssignedStaffFilter,
    primaryContactEmails: appliedPrimaryContactFilter,
  };
  const activeFilterCount = countActiveFilters(activeFilters);
  const drawerResultCount = useMemo(() => {
    const draftQuery = (advisers || []).filter((adviser) => matchesQuery(adviser, q));
    return draftQuery.filter((adviser) => {
      if (draftFilters.unappointedOnly) {
        if (adviser.isAppointed) return false;
      } else if (!adviser.isAppointed) {
        return false;
      } else if (draftFilters.arOnly) {
        if (!adviser.isAR || adviser.isIndroducerAR) return false;
      } else if (draftFilters.iarOnly) {
        if (!adviser.isIndroducerAR) return false;
      } else if (draftFilters.nonArOnly) {
        if (adviser.isAR || adviser.isIndroducerAR) return false;
      }

      const adviserStatuses = Array.isArray(adviser.status) ? adviser.status : [];
      if (
        draftFilters.selectedStatuses.length &&
        !draftFilters.selectedStatuses.every((status) => adviserStatuses.includes(status))
      ) {
        return false;
      }

      if (draftFilters.tradingStyle) {
        const tradingValues = [
          adviser.tradingStyle,
          adviser.tradingName,
          ...(adviser.tradingStyles || []),
        ].map(norm);
        if (!tradingValues.includes(norm(draftFilters.tradingStyle))) return false;
      }

      if (draftFilters.businessArea) {
        const areas = [
          ...(adviser.businessAreas || []),
          ...(adviser.ARBusinessAreas || []),
        ].map(norm);
        if (!areas.includes(norm(draftFilters.businessArea))) return false;
      }

      const catchUpData = catchUpAssignments[adviser.id] || {};
      const assignedEmails = resolveAssignedStaffEmails(catchUpData, adviser);
      const primaryEmail = resolveAdviserCatchUpPrimaryEmail(catchUpData, adviser);
      if (
        draftFilters.assignedStaffEmails.length &&
        !draftFilters.assignedStaffEmails.some((email) => assignedEmails.includes(email))
      ) {
        return false;
      }
      if (!matchesPrimaryContactFilter(draftFilters.primaryContactEmails, primaryEmail)) {
        return false;
      }

      return true;
    }).length;
  }, [advisers, q, draftFilters, catchUpAssignments]);

  useEffect(() => {
    sessionStorage.setItem(
      ADVISER_FILTERS_KEY,
      JSON.stringify({
        arOnly,
        iarOnly,
        nonArOnly,
        unappointedOnly,
        selectedStatuses,
        tradingStyle: tradingStyleFilter,
        businessArea: businessAreaFilter,
        assignedStaffEmails: appliedAssignedStaffFilter,
        primaryContactEmails: appliedPrimaryContactFilter,
      }),
    );
  }, [
    arOnly,
    iarOnly,
    nonArOnly,
    unappointedOnly,
    selectedStatuses,
    tradingStyleFilter,
    businessAreaFilter,
    appliedAssignedStaffFilter,
    appliedPrimaryContactFilter,
  ]);

  useEffect(() => {
    setLoading(true);
    return subscribeAdvisersData(
      (data) => {
        setAdvisers(data || []);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!advisers.length) {
      setCatchUpAssignments({});
      return undefined;
    }

    Promise.all(
      advisers.map(async (adviser) => {
        try {
          return [adviser.id, await getAdviserCatchUpAssignmentData(adviser.id)];
        } catch {
          return [adviser.id, {}];
        }
      }),
    ).then((entries) => {
      if (!cancelled) setCatchUpAssignments(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [advisers]);

  const filtered = useMemo(() => {
    return (advisers || [])
      .filter((a) => matchesQuery(a, q))
      .filter((a) => {
        if (unappointedOnly) return !a.isAppointed;
        if (!a.isAppointed) return false;
        if (arOnly) return !!a.isAR && !a.isIndroducerAR;
        if (iarOnly) return !!a.isIndroducerAR;
        if (nonArOnly) return !a.isAR && !a.isIndroducerAR;
        return true;
      })
      .filter((a) => {
        if (selectedStatuses.length === 0) return true;

        const st = Array.isArray(a.status) ? a.status : [];
        return selectedStatuses.every((s) => st.includes(s)); // AND
      })
      .filter((a) => {
        if (!tradingStyleFilter) return true;

        const tradingValues = [
          a.tradingStyle,
          a.tradingName,
          ...(a.tradingStyles || []),
        ].map((value) => norm(value));

        return tradingValues.includes(norm(tradingStyleFilter));
      })
      .filter((a) => {
        if (!businessAreaFilter) return true;

        const businessValues = [
          ...(a.businessAreas || []),
          ...(a.ARBusinessAreas || []),
        ].map((value) => norm(value));

        return businessValues.includes(norm(businessAreaFilter));
      })
      .filter((a) => {
        if (!appliedAssignedStaffFilter.length && !appliedPrimaryContactFilter.length) return true;
        const catchUpData = catchUpAssignments[a.id] || {};
        const assignedEmails = resolveAssignedStaffEmails(catchUpData, a);
        const primaryEmail = resolveAdviserCatchUpPrimaryEmail(catchUpData, a);
        const assignedMatch =
          !appliedAssignedStaffFilter.length ||
          appliedAssignedStaffFilter.some((email) => assignedEmails.includes(email));
        const primaryMatch = matchesPrimaryContactFilter(
          appliedPrimaryContactFilter,
          primaryEmail,
        );
        return assignedMatch && primaryMatch;
      })
      .sort((a, b) => {
        const sortNameA = a.preferredName || a.firstName || a.email || "";
        const sortNameB = b.preferredName || b.firstName || b.email || "";
        const nameComparison = sortNameA.localeCompare(
          sortNameB,
          undefined,
          { sensitivity: "base" },
        );

        if (nameComparison !== 0) return nameComparison;
        return (a.email || "").localeCompare(b.email || "", undefined, {
          sensitivity: "base",
        });
      });
  }, [
    advisers,
    q,
    selectedStatuses,
    arOnly,
    iarOnly,
    nonArOnly,
    unappointedOnly,
    tradingStyleFilter,
    businessAreaFilter,
    appliedAssignedStaffFilter,
    appliedPrimaryContactFilter,
    catchUpAssignments,
  ]);

  useEffect(() => {
    if (loading || scrollRestoredRef.current || !listScrollRef.current) return;

    const savedScrollTop = Number(
      sessionStorage.getItem(ADVISER_LIST_SCROLL_KEY) || 0,
    );
    listScrollRef.current.scrollTop = Number.isFinite(savedScrollTop)
      ? savedScrollTop
      : 0;
    scrollRestoredRef.current = true;
  }, [loading, filtered.length]);

  const rememberListScroll = (event) => {
    sessionStorage.setItem(
      ADVISER_LIST_SCROLL_KEY,
      String(event.currentTarget.scrollTop),
    );
  };

  const selectAdviser = (adviserId) => {
    setSelectedId(adviserId);
    sessionStorage.setItem(SELECTED_ADVISER_KEY, adviserId);
  };
  // useEffect(() => {
  //   if (arOnly) setNonArOnly(false);
  // }, [arOnly]);

  // useEffect(() => {
  //   if (nonArOnly) setArOnly(false);
  // }, [nonArOnly]);

  const selected = useMemo(() => {
    return (
      filtered.find((a) => a.id === selectedId) ||
      advisers.find((a) => a.id === selectedId) ||
      null
    );
  }, [filtered, advisers, selectedId]);

  const selectedLogo = selected?.brandName
    ? logoCache.get(String(selected.brandName).trim().toLowerCase())
    : "";
  const canEditRegulatoryText =
    (userPermissions || []).includes("Compliance Support") ||
    (userPermissions || []).includes("Administrator");

  const handleRegulatoryTextSaved = (adviserId, regulatoryText) => {
    setAdvisers((prev) =>
      (prev || []).map((a) =>
        a.id === adviserId ? { ...a, regulatoryText } : a,
      ),
    );
  };

  const handleDownloadAdviserColours = async () => {
    setDownloadingColours(true);
    try {
      const { blob, filename } = await exportAdviserColoursExcel(advisers);
      saveAs(blob, filename);
    } finally {
      setDownloadingColours(false);
    }
  };

  const handleDownloadAllLogos = async () => {
    setDownloadingLogos(true);
    setLogoDownloadError("");
    try {
      const { blob, filename } = await exportAdviserLogosZip();
      saveAs(blob, filename);
    } catch (error) {
      setLogoDownloadError(error?.message || "Could not download adviser logos.");
    } finally {
      setDownloadingLogos(false);
    }
  };

  const handleDownloadArFcaNumbers = async () => {
    setDownloadingArFcaNumbers(true);
    try {
      const { blob, filename } = await exportArFcaNumbersExcel(advisers);
      saveAs(blob, filename);
    } finally {
      setDownloadingArFcaNumbers(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selected?.brandName) return;

      const key = String(selected.brandName).trim().toLowerCase();
      if (!key || logoCache.has(key)) return;

      try {
        const url = await fetchLogoUrl(selected.brandName);
        if (!alive) return;

        setLogoCache((prev) => {
          const next = new Map(prev);
          next.set(key, url || "");
          return next;
        });
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [selected?.brandName]);

return (
  <div className="main-content">
    <Helmet>
      <title>Advisers</title>
      <meta
        name="description"
        content="View and manage enquiries, including customer and adviser requests."
      />
    </Helmet>
    <Sidebar />
    <div className="Main-Container">
      <div className="advPage">
        <Breadcrumb
          items={[
            {
              label: "All Staff",
              to: "/Department/AllStaff",
            },
            { label: "Advisers" },
          ]}
        />
        <div className="advHeader">
          <div className="advTitle">
            <h2>Advisers</h2>
            <h4>{loading ? "Loading…" : `${filtered.length}`}</h4>
          </div>
          <div className="row gap-m align-center">
            {/* {canEditRegulatoryText ? (
              <PrimaryButton
                type="button"
                onClick={() => navigate("/advisers/regulatory-wording-updates")}
                label="Update regulatory wording"
              />
            ) : null} */}
            {/* <PrimaryButton
              type="button"
              onClick={handleDownloadArFcaNumbers}
              disabled={
                downloadingArFcaNumbers || loading || advisers.length === 0
              }
              label={
                downloadingArFcaNumbers
                  ? "Downloading..."
                  : "Download AR FCA Excel"
              }
            /> */}
            {userPermissions.includes("Administrator") ? (
              <PrimaryButton
                onClick={() => navigate("/audit-logs")}
                label="Edit logs"
              ></PrimaryButton>
            ) : // <HistoryIcon onClick={() => navigate("/audit-logs")} />
            null}


            {/* <PrimaryButton
              type="button"
              onClick={handleDownloadAllLogos}
              disabled={downloadingLogos}
              label={downloadingLogos ? "Preparing logos..." : "Download all logos"}
            /> */}

            {/* <PrimaryButton
              type="button"
              onClick={clearAllFilters}
              disabled={
                activeFilterCount === 0
              }
              label="Clear"
            /> */}
          </div>
          {logoDownloadError ? (
            <div className="Content-Small advLogoDownloadError" role="alert">
              {logoDownloadError}
            </div>
          ) : null}
          {/* <PrimaryButton
            onClick={handleDownloadAdviserColours}
            label={downloadingColours ? "Downloading..." : "Download adviser emails & colours"}
            disabled={downloadingColours || loading || advisers.length === 0}
          /> */}
          {/* <PrimaryButton
            onClick={downloadTempAdviserDobCsv}
            label="Download DOB CSV"
          /> */}
        </div>
        <header className="adviserDirectoryToolbar">
          <div className="advFilters">
            <div className="advFilterToolbar">
              <button
                type="button"
                className="advOpenFilters Content-Small"
                onClick={openFilters}
                aria-haspopup="dialog"
                aria-expanded={filtersOpen}
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </button>
              <span className="advFilterResultCount Content-Small" aria-live="polite">
                {loading ? "Loading advisers…" : `${filtered.length} advisers`}
              </span>
            </div>
            {/* <div className="advFilterRow">
              <label className="advFilterField Content-Small">
                <span>Trading style</span>
                <select
                  value={tradingStyleFilter}
                  onChange={(event) => setTradingStyleFilter(event.target.value)}
                  disabled={loading || tradingStyleOptions.length === 0}
                >
                  <option value="">All trading styles</option>
                  {tradingStyleOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="advFilterField Content-Small">
                <span>Business permission</span>
                <select
                  value={businessAreaFilter}
                  onChange={(event) => setBusinessAreaFilter(event.target.value)}
                  disabled={loading || businessAreaOptions.length === 0}
                >
                  <option value="">All permissions</option>
                  {businessAreaOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            </div> */}
            <div className="advStatusFilter">
              <div className="advStatusChips">
                <label className={`advChip ${arOnly ? "isOn" : ""}`}>
                  <input type="checkbox" checked={arOnly} onChange={toggleArOnly} />
                  <span className="Content-Small">AR</span>
                </label>
                <label className={`advChip ${iarOnly ? "isOn" : ""}`}>
                  <input type="checkbox" checked={iarOnly} onChange={toggleIarOnly} />
                  <span className="Content-Small">IAR</span>
                </label>
                <label className={`advChip ${nonArOnly ? "isOn" : ""}`}>
                  <input type="checkbox" checked={nonArOnly} onChange={toggleNonArOnly} />
                  <span className="Content-Small">TS</span>
                </label>
                {statusOptions.map((status) => (
                  <label key={status} className={`advChip ${selectedStatuses.includes(status) ? "isOn" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => setSelectedStatuses((previous) => toggleValue(previous, status))}
                    />
                    <span className="Content-Small">{status}</span>
                  </label>
                ))}
                <label className={`advChip ${unappointedOnly ? "isOn" : ""}`}>
                  <input type="checkbox" checked={unappointedOnly} onChange={toggleUnappointedOnly} />
                  <span className="Content-Small">Unappointed/Leavers</span>
                </label>
              </div>
            </div>
            {activeFilterCount > 0 ? (
              <div className="advActiveFilters" aria-label="Active filters">
                {arOnly && <button type="button" onClick={() => setArOnly(false)}>AR <span aria-hidden="true">×</span></button>}
                {iarOnly && <button type="button" onClick={() => setIarOnly(false)}>IAR <span aria-hidden="true">×</span></button>}
                {nonArOnly && <button type="button" onClick={() => setNonArOnly(false)}>TS <span aria-hidden="true">×</span></button>}
                {unappointedOnly && <button type="button" onClick={() => setUnappointedOnly(false)}>Unappointed/Leavers <span aria-hidden="true">×</span></button>}
                {selectedStatuses.map((status) => (
                  <button key={status} type="button" onClick={() => setSelectedStatuses((prev) => prev.filter((item) => item !== status))}>
                    {status} <span aria-hidden="true">×</span>
                  </button>
                ))}
                {tradingStyleFilter && <button type="button" onClick={() => setTradingStyleFilter("")}>{tradingStyleFilter} <span aria-hidden="true">×</span></button>}
                {businessAreaFilter && <button type="button" onClick={() => setBusinessAreaFilter("")}>{businessAreaFilter} <span aria-hidden="true">×</span></button>}
                {assignedStaffFilter.map((email) => (
                  <button key={`staff-${email}`} type="button" onClick={() => setAssignedStaffFilter((previous) => previous.filter((item) => item !== email))}>
                    Staff: {formatAdviserCatchUpPersonName(email)} <span aria-hidden="true">×</span>
                  </button>
                ))}
                {primaryContactFilter.map((email) => (
                  <button key={`primary-${email}`} type="button" onClick={() => setPrimaryContactFilter((previous) => previous.filter((item) => item !== email))}>
                    Primary: {email === NO_PRIMARY_CONTACT_FILTER ? "None" : formatAdviserCatchUpPersonName(email)} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <input
            className="advSearch Content-Small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, trading style, FRN number…"
          />
        </header>
        <AdviserFiltersDrawer
          open={filtersOpen}
          filters={draftFilters}
          statusOptions={statusOptions}
          tradingStyleOptions={tradingStyleOptions}
          businessAreaOptions={businessAreaOptions}
          contactOptions={ADVISER_CATCH_UP_POINTS_OF_CONTACT}
          showCatchUpFilters={canSeeCatchUpFilters}
          resultCount={drawerResultCount}
          onClose={() => setFiltersOpen(false)}
          onChange={setDraftFilters}
          onApply={(nextFilters) => {
            applyDraftFilters(nextFilters);
            setFiltersOpen(false);
          }}
          onReset={() => setDraftFilters(EMPTY_ADVISER_FILTERS)}
        />

        <main className="advGrid">
          {/* LEFT: list */}
          <section className="advList">
            {loading ? (
              <div className="advEmpty Content-Small">Loading advisers…</div>
            ) : filtered.length === 0 ? (
              <div className="advEmpty Content-Small">
                No advisers match your search.
              </div>
            ) : (
              <div
                className="advScroll"
                ref={listScrollRef}
                onScroll={rememberListScroll}
              >
                {filtered.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`advRow ${
                      a.id === selectedId ? "isActive" : ""
                    }`}
                    style={{
                      borderLeft: `4px solid ${
                        a.primaryColor
                          ? String(a.primaryColor).startsWith("#")
                            ? a.primaryColor
                            : `#${a.primaryColor}`
                          : "var(--Brand1-PenBlue)"
                      }`,
                    }}
                    onClick={() => selectAdviser(a.id)}
                  >
                    <div
                      className="advAvatar Content-SemiBold"
                      style={{
                        background: cssColor(
                          a.primaryColor,
                          "var(--Brand1-PenBlue)",
                        ),
                        color: "var(--White)",
                      }}
                    >
                      {initialsFrom(a)}
                    </div>

                    <div className="advRowMain">
                      <div className="advRowTop">
                        <span className="advName Content-SemiBold">
                          {displayName(a)}
                        </span>
                        {(a.isAR || a.isIndroducerAR) && (
                          <span className="advTypeBadges">
                            {a.isAR ? <span className="badge">AR</span> : null}
                            {a.isIndroducerAR ? (
                              <span className="badge">IAR</span>
                            ) : null}
                          </span>
                        )}
                      </div>

                      <div className="profileMeta advSub Content-Small">
                        <span className="muted">
                          {a.tradingStyle ||
                            a.tradingName ||
                            a.tradingStyles?.[0] ||
                            "-"}
                        </span>
                        {a.fcaNumber ? <span className="dot">•</span> : null}
                        {a.fcaNumber ? (
                          <span className="muted">FRN {a.fcaNumber}</span>
                        ) : null}
                      </div>

                      {!!a.status?.length && (
                        <div className="advTags">
                          {a.status.slice(0, 3).map((s) => (
                            <span className="tag" key={s}>
                              {s}
                            </span>
                          ))}
                          {a.status.length > 3 ? (
                            <span className="tag muted Content-Small">
                              +{a.status.length - 3}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* RIGHT: profile */}
          <section className="advProfile">
            {selected ? (
              <MinimalProfile
                adviser={{ ...selected, logoUrl: selectedLogo || "" }}
                onRegulatoryTextSaved={handleRegulatoryTextSaved}
                canEditRegulatoryText={canEditRegulatoryText}
              />
            ) : null}
          </section>
        </main>
      </div>
    </div>
  </div>
);
}

function AdviserFiltersDrawer({
  open,
  filters,
  statusOptions,
  tradingStyleOptions,
  businessAreaOptions,
  contactOptions,
  showCatchUpFilters,
  resultCount,
  onClose,
  onChange,
  onApply,
  onReset,
}) {
  const [tradingStyleQuery, setTradingStyleQuery] = useState("");
  const [businessAreaQuery, setBusinessAreaQuery] = useState("");
  const [statusQuery, setStatusQuery] = useState("");
  const [contactQuery, setContactQuery] = useState("");

  useEffect(() => {
    if (open) {
      setTradingStyleQuery("");
      setBusinessAreaQuery("");
      setStatusQuery("");
      setContactQuery("");
    }
  }, [open]);

  const setFilter = (field, value) =>
    onChange((previous) => ({ ...previous, [field]: value }));
  const setTypeFilter = (field, value) =>
    onChange((previous) => {
      const next = { ...previous, [field]: value };
      if (value && field !== "unappointedOnly") {
        next.arOnly = field === "arOnly";
        next.iarOnly = field === "iarOnly";
        next.nonArOnly = field === "nonArOnly";
        next.unappointedOnly = false;
      } else if (value && field === "unappointedOnly") {
        next.arOnly = false;
        next.iarOnly = false;
        next.nonArOnly = false;
        next.selectedStatuses = [];
      }
      return next;
    });
  const toggleStatus = (status) =>
    onChange((previous) => ({
      ...previous,
      selectedStatuses: toggleValue(previous.selectedStatuses, status),
    }));
  const normalizedMatch = (label, query) =>
    norm(label).includes(norm(query));
  const matchesContactQuery = (email) =>
    normalizedMatch(email, contactQuery) ||
    normalizedMatch(formatAdviserCatchUpPersonName(email), contactQuery);

  return (
    <FilterDrawer
      open={open}
      title="Adviser filters"
      onClose={onClose}
      className="AdviserFilterDrawer"
      footer={
        <>
          <FilterDrawerApplyButton onClick={() => onApply(filters)}>
            Show {resultCount} advisers
          </FilterDrawerApplyButton>
          <FilterDrawerResetButton onClick={onReset}>
            Reset all filters
          </FilterDrawerResetButton>
        </>
      }
    >
      <FilterDrawerSection title="Adviser type">
        <FilterDrawerPills>
          {[
            ["arOnly", "AR"],
            ["iarOnly", "IAR"],
            ["nonArOnly", "TS"],
            ["unappointedOnly", "Unappointed / leavers"],
          ].map(([field, label]) => (
            <FilterDrawerPill
              key={field}
              active={filters[field]}
              onClick={() => setTypeFilter(field, !filters[field])}
            >
              {label}
            </FilterDrawerPill>
          ))}
        </FilterDrawerPills>
      </FilterDrawerSection>

      <FilterDrawerSection title="Status">
        {/* <input
          className="AdviserFilterDrawer__Search Content-Small"
          type="search"
          value={statusQuery}
          onChange={(event) => setStatusQuery(event.target.value)}
          placeholder="Search statuses"
          aria-label="Search statuses"
        /> */}
        {statusOptions.filter((option) => normalizedMatch(option, statusQuery)).length ? (
          statusOptions
            .filter((option) => normalizedMatch(option, statusQuery))
            .map((status) => (
              <FilterDrawerCheckboxOption
                key={status}
                label={status}
                checked={filters.selectedStatuses.includes(status)}
                onChange={() => toggleStatus(status)}
              />
            ))
        ) : (
          <FilterDrawerEmpty>No matching statuses</FilterDrawerEmpty>
        )}
      </FilterDrawerSection>

      {/* <FilterDrawerSection title="Assigned staff">
        <input
          className="AdviserFilterDrawer__Search Content-Small"
          type="search"
          value={contactQuery}
          onChange={(event) => setContactQuery(event.target.value)}
          placeholder="Search staff"
          aria-label="Search assigned staff"
        />
        {contactOptions.filter(matchesContactQuery).length ? (
          contactOptions
            .filter(matchesContactQuery)
            .map((email) => (
              <FilterDrawerCheckboxOption
                key={`assigned-${email}`}
                label={formatAdviserCatchUpPersonName(email)}
                checked={filters.assignedStaffEmails.includes(email)}
                onChange={() =>
                  setFilter("assignedStaffEmails", toggleValue(filters.assignedStaffEmails, email))
                }
              />
            ))
        ) : (
          <FilterDrawerEmpty>No matching staff</FilterDrawerEmpty>
        )}
      </FilterDrawerSection> */}

      {showCatchUpFilters ? <FilterDrawerSection title="Primary point of contact">
        {!contactQuery || normalizedMatch("None", contactQuery) ? (
          <FilterDrawerCheckboxOption
            label="None"
            checked={filters.primaryContactEmails.includes(NO_PRIMARY_CONTACT_FILTER)}
            onChange={() =>
              setFilter(
                "primaryContactEmails",
                toggleValue(filters.primaryContactEmails, NO_PRIMARY_CONTACT_FILTER),
              )
            }
          />
        ) : null}
        {contactOptions.filter(matchesContactQuery).length ? (
          contactOptions
            .filter(matchesContactQuery)
            .map((email) => (
              <FilterDrawerCheckboxOption
                key={`primary-${email}`}
                label={formatAdviserCatchUpPersonName(email)}
                checked={filters.primaryContactEmails.includes(email)}
                onChange={() =>
                  setFilter("primaryContactEmails", toggleValue(filters.primaryContactEmails, email))
                }
              />
            ))
        ) : (
          <FilterDrawerEmpty>No matching points of contact</FilterDrawerEmpty>
        )}
      </FilterDrawerSection> : null}

      <FilterDrawerSection title="Trading style or name">
        <input
          className="AdviserFilterDrawer__Search Content-Small"
          type="search"
          value={tradingStyleQuery}
          onChange={(event) => setTradingStyleQuery(event.target.value)}
          placeholder="Find a trading style or name"
          aria-label="Search trading styles or names"
        />
        <FilterDrawerPills>
          <FilterDrawerPill
            active={!filters.tradingStyle}
            onClick={() => setFilter("tradingStyle", "")}
          >
            Any
          </FilterDrawerPill>
          {tradingStyleOptions
            .filter((option) => normalizedMatch(option, tradingStyleQuery))
            .map((option) => (
              <FilterDrawerPill
                key={option}
                active={filters.tradingStyle === option}
                onClick={() =>
                  setFilter("tradingStyle", filters.tradingStyle === option ? "" : option)
                }
              >
                {option}
              </FilterDrawerPill>
            ))}
        </FilterDrawerPills>
        {!tradingStyleOptions.some((option) => normalizedMatch(option, tradingStyleQuery)) ? (
          <FilterDrawerEmpty>No matching trading styles or names</FilterDrawerEmpty>
        ) : null}
      </FilterDrawerSection>

      {/* <FilterDrawerSection title="Business permission">
        <input
          className="AdviserFilterDrawer__Search Content-Small"
          type="search"
          value={businessAreaQuery}
          onChange={(event) => setBusinessAreaQuery(event.target.value)}
          placeholder="Find a permission"
          aria-label="Search business permissions"
        />
        <FilterDrawerPills>
          <FilterDrawerPill
            active={!filters.businessArea}
            onClick={() => setFilter("businessArea", "")}
          >
            Any
          </FilterDrawerPill>
          {businessAreaOptions
            .filter((option) => normalizedMatch(option, businessAreaQuery))
            .map((option) => (
              <FilterDrawerPill
                key={option}
                active={filters.businessArea === option}
                onClick={() =>
                  setFilter("businessArea", filters.businessArea === option ? "" : option)
                }
              >
                {option}
              </FilterDrawerPill>
            ))}
        </FilterDrawerPills>
        {!businessAreaOptions.some((option) => normalizedMatch(option, businessAreaQuery)) ? (
          <FilterDrawerEmpty>No matching business permissions</FilterDrawerEmpty>
        ) : null}
      </FilterDrawerSection> */}
    </FilterDrawer>
  );
}

function MinimalProfile({
  adviser,
  onRegulatoryTextSaved,
  canEditRegulatoryText,
}) {
  const navigate = useNavigate();
  const name = displayName(adviser);
  const [regulatoryDraft, setRegulatoryDraft] = useState(
    adviser?.regulatoryText || "",
  );
  const [regulatorySaving, setRegulatorySaving] = useState(false);
  const [regulatoryError, setRegulatoryError] = useState("");
  const [regulatorySaved, setRegulatorySaved] = useState(false);
  const [isProfilePhotoOpen, setIsProfilePhotoOpen] = useState(false);

  useEffect(() => {
    setRegulatoryDraft(adviser?.regulatoryText || "");
    setRegulatoryError("");
    setRegulatorySaved(false);
    setIsProfilePhotoOpen(false);
  }, [adviser?.id, adviser?.regulatoryText]);

  useEffect(() => {
    if (!isProfilePhotoOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsProfilePhotoOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isProfilePhotoOpen]);

  const hasRegulatoryChanges =
    regulatoryDraft !== String(adviser?.regulatoryText || "");

  const saveRegulatoryText = async () => {
    if (!adviser?.id || !hasRegulatoryChanges || regulatorySaving) return;

    setRegulatorySaving(true);
    setRegulatoryError("");
    setRegulatorySaved(false);

    try {
      const savedValue = await updateAdviserRegulatoryText(
        adviser.id,
        regulatoryDraft,
      );
      if (typeof onRegulatoryTextSaved === "function") {
        onRegulatoryTextSaved(adviser.id, savedValue);
      }
      setRegulatorySaved(true);
    } catch {
      setRegulatoryError("Could not save regulatory text. Please try again.");
    } finally {
      setRegulatorySaving(false);
    }
  };

  const bannerText =
    adviser.tradingStyle ||
    adviser.tradingName ||
    adviser.tradingStyles?.[0] ||
    "";

  const bannerColor = adviser?.primaryColor
    ? `${String(adviser.primaryColor).startsWith("#") ? "" : "#"}${
        adviser.primaryColor
      }`
    : "var(--Brand1-PenBlue)";

  return (
    <div className="profileWrap">
      <div className="profileBanner" style={{ background: bannerColor }}>
        <div className="profileBannerText SubTitle">{bannerText}</div>
        {adviser.logoUrl && (
          <div className="profileHeaderRight">
            <div className="profileLogoRight">
              <img
                className="smLogoImg-directory"
                src={adviser.logoUrl}
                alt="Logo"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "";
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
            {/* <img
                src={adviser.logoUrl}
                alt="Logo"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null; // stop loops
                  e.currentTarget.src = ""; // remove request
                  e.currentTarget.style.display = "none";
                }}
              /> */}
          </div>
        )}
      </div>

      <div className="profileHeader">
        {/* LEFT COLUMN */}
        <div className="profileHeaderLeft">
          <h2 className="profileName Content-SemiBold">{name}</h2>

          <div className="profileMeta Content-Small">
            <span className="muted">{adviser.email || "-"}</span>
            {adviser.mobileNumber && <span className="dot">•</span>}
            {adviser.mobileNumber && (
              <span className="muted">{adviser.mobileNumber}</span>
            )}
          </div>

          <div className="profileActionsRow">
            {adviser.isAR && <span className="badge Content-Small">AR</span>}
            {adviser.isIndroducerAR && (
              <span className="badge Content-Small">IAR</span>
            )}

            {adviser.fcaNumber ? (
              <span className="badge soft Content-Small">
                FRN {adviser.fcaNumber}
              </span>
            ) : null}
          </div>
          <div className="profileActionsRow">
            {adviser.email ? (
              <a className="btn Content-Small" href={`mailto:${adviser.email}`}>
                Email
              </a>
            ) : (
              <button className="btn Content-Small" disabled>
                Email
              </button>
            )}

            {adviser.mobileNumber ? (
              <a
                className="btn Content-Small"
                href={`tel:${String(adviser.mobileNumber).replace(/\s/g, "")}`}
              >
                Call
              </a>
            ) : (
              <button className="btn Content-Small" disabled>
                Call
              </button>
            )}
            {adviser.website && (
              <a
                className="btn Content-Small"
                href={adviser.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                Website
              </a>
            )}
            {/* <a
              className="btn Content-Small"
              href={`/advisers/${encodeURIComponent(adviser.id)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View details
            </a> */}
            <button
              type="button"
              className="btn Content-Small"
              onClick={() =>
                navigate(`/advisers/${encodeURIComponent(adviser.id)}`, {
                  state: {
                    // keep existing keys (backward compatible)
                    name,
                    email: adviser.email || "",
                    logoUrl: adviser.logoUrl || "",
                    primaryColor: adviser.primaryColor || "",
                    tradingName: adviser.tradingName || "",
                    tradingStyle: adviser.tradingStyle || "",
                    tradingStyles: adviser.tradingStyles || [],
                    isAR: !!adviser.isAR,
                    isIndroducerAR: !!adviser.isIndroducerAR,
                    fcaNumber: adviser.fcaNumber || "",

                    // “missing” bits
                    status: adviser.status || [],
                    mobileNumber: adviser.mobileNumber || "",
                    officeNumber: adviser.officeNumber || "",
                    businessAddress: adviser.businessAddress || "",
                    preferredName: adviser.preferredName || "",
                    firstName: adviser.firstName || "",
                    lastName: adviser.lastName || "",

                    // optional
                    adviserId: adviser.id || "",
                    raw: adviser, // optional
                    businessAreas: adviser.businessAreas || [],
                    ARBusinessAreas: adviser.ARBusinessAreas || [],
                  },
                })
              }
              disabled={!adviser?.id}
              // title={!adviser?.id ? "No adviser id on record" : "View details"}
            >
              View details
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        {adviser.profileUrl && (
          <div className="profileHeaderRight">
            <button
              type="button"
              className="profileLogoRight-directory"
              onClick={() => setIsProfilePhotoOpen(true)}
              aria-label={`View a larger photo of ${name}`}
              title="View larger photo"
            >
              <img
                src={adviser.profileUrl}
                alt={`${name} profile`}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null; // stop loops
                  e.currentTarget.src = ""; // remove request
                  e.currentTarget.style.display = "none";
                }}
              />
            </button>
          </div>
        )}
      </div>

      {isProfilePhotoOpen && (
        <div
          className="adviserPhotoModal"
          role="dialog"
          aria-modal="true"
          aria-label={`${name} profile photo`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsProfilePhotoOpen(false);
            }
          }}
        >
          <div className="adviserPhotoModalContent">
            <button
              type="button"
              className="adviserPhotoModalClose"
              onClick={() => setIsProfilePhotoOpen(false)}
              aria-label="Close photo"
            >
              &times;
            </button>
            <img src={adviser.profileUrl} alt={`${name} profile`} />
          </div>
        </div>
      )}

      {/* remove old profileActions block (now inside header) */}

      <div className="profileCards">
        <div className="card">
          {/* <div className="cardActions">
            <button
              type="button"
              className="btn Content-Small"
              onClick={() =>
                navigate(
                  `/advisers/${encodeURIComponent(adviser.email)}/details`,
                  {
                    state: {
                      // optional: pass some basics so page can render header instantly
                      name,
                      logoUrl: adviser.logoUrl || "",
                      primaryColor: adviser.primaryColor || "",
                      tradingName: adviser.tradingName || "",
                      tradingStyle: adviser.tradingStyle || "",
                      tradingStyles: adviser.tradingStyles || [],
                      isAR: adviser.isAR || false,
                      fcaNumber: adviser.fcaNumber || "",
                    },
                  }
                )
              }
              disabled={!adviser.email}
              title={
                !adviser.email
                  ? "No email on record"
                  : "View recruitment details"
              }
            >
              View details
            </button>
          </div> */}

          <div className="kv">
            <span className="Content-Small">Status</span>
            <b className="wrap Content">
              {(adviser.status || []).join(", ") || "-"}
            </b>
          </div>

          <div className="kv">
            <span className="Content-Small">Trading Name</span>
            <b className="wrap Content">{adviser.tradingName || "-"}</b>
          </div>

          <div className="kv">
            <span className="Content-Small">Trading Styles</span>
            <b className="wrap Content">
              {(adviser.tradingStyles || []).join(", ") || "-"}
            </b>
          </div>
        </div>

        <div className="card">
          <div className="kv">
            <span className="Content-Small">Business Address</span>
            <b className="wrap Content">{adviser.businessAddress || "-"}</b>
          </div>
          <div className="kv">
            <span className="Content-Small">Office Telephone</span>
            <b className="wrap Content">{adviser.officeNumber || "-"}</b>
          </div>
        </div>
        <div className="card">
          <div className="kv">
            <span className="Content-Small">Business permissions</span>
            <b className="wrap Content">
              {(adviser.businessAreas || []).join(", ") || "-"}
            </b>
          </div>

          {adviser.isAR && (
            <div className="kv">
              <span className="Content-Small">AR Business permissions</span>
              <b className="wrap Content">
                {(adviser.ARBusinessAreas || []).join(", ") || "-"}
              </b>
            </div>
          )}
        </div>
        <div className="card">
          <div className="kv">
            <span className="Content-Small">Regulatory Text</span>
          </div>
          <textarea
            className="advSearch Content-Small advRegulatoryTextarea"
            value={regulatoryDraft}
            onChange={(e) => {
              setRegulatoryDraft(e.target.value);
              setRegulatorySaved(false);
            }}
            placeholder="Add or edit regulatory text"
            rows={8}
          />

          {/* {canEditRegulatoryText ? (
            <div className="advRegulatoryActions">
              <button
                type="button"
                className="btn Content-Small"
                onClick={saveRegulatoryText}
                disabled={
                  !adviser?.id || !hasRegulatoryChanges || regulatorySaving
                }
              >
                {regulatorySaving ? "Saving..." : "Save"}
              </button>

              {regulatorySaved && !regulatoryError ? (
                <span className="Content-Small advRegulatorySaved">Saved</span>
              ) : null}

              {regulatoryError ? (
                <span className="Content-Small advRegulatoryError">
                  {regulatoryError}
                </span>
              ) : null}
            </div>
          ) : null} */}
        </div>
      </div>
    </div>
  );
}

